/**
 * EmptyCellHelpLink — the subtle "help us improve" affordance that
 * every empty course-page cell renders instead of a bare `—`.
 * Posts to `POST /api/data-gap-report` so ops has a queue of what
 * families found missing.
 *
 * Rewrite (Feb 2026): swapped the ugly `window.prompt()` for a
 * proper Shadcn Dialog. Added optional "your relation to the
 * university" select + email input with a credibility note.
 * Multi-line textarea for the actual hint. Field-level context
 * ("Help us fix Annual fee growth") shown in the dialog header
 * so the reporter never wonders which cell they clicked.
 *
 * Deliberately low-key trigger — 10px purple text, hover-underline
 * — so it never looks broken; families who don't care never notice
 * it, ones who do can flag it in a proper form.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const RELATION_OPTIONS = [
  { value: "prospective", label: "Prospective student" },
  { value: "student",     label: "Current student" },
  { value: "alumni",      label: "Alumni" },
  { value: "parent",      label: "Parent or guardian" },
  { value: "faculty",     label: "Faculty / academic staff" },
  { value: "admin",       label: "Administrative staff" },
  { value: "counsellor",  label: "Counsellor / advisor" },
  { value: "other",       label: "Other or no relation" },
];

export default function EmptyCellHelpLink({
  degreeId,
  universityId,
  section,
  field,
  label = "help us improve",
}) {
  const [open, setOpen] = useState(false);
  const [hint, setHint] = useState("");
  const [email, setEmail] = useState("");
  const [relation, setRelation] = useState("");
  const [posting, setPosting] = useState(false);
  const [sent, setSent] = useState(false);

  const reset = () => {
    setHint("");
    setEmail("");
    setRelation("");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (posting) return;
    setPosting(true);
    try {
      const res = await fetch(`${API}/api/data-gap-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          degree_id: degreeId,
          university_id: universityId,
          section,
          field,
          hint: hint.trim() || null,
          contact_email: email.trim() || null,
          reporter_relation: relation || null,
        }),
      });
      if (!res.ok) throw new Error("bad status");
      setSent(true);
      setOpen(false);
      reset();
      toast.success("Thanks — logged for the ops team.");
    } catch {
      toast.error("Couldn't send just now — try again in a bit.");
    } finally {
      setPosting(false);
    }
  };

  if (sent) {
    return (
      <span
        data-testid="empty-cell-help-link-sent"
        className="text-[10px] text-emerald-700"
      >
        Thanks — logged ✓
      </span>
    );
  }

  const fieldLabel = field || "this cell";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="empty-cell-help-link"
        className="text-[10px] text-purple-700/70 hover:underline hover:text-purple-800 cursor-pointer"
      >
        {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid="empty-cell-help-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-editorial text-2xl leading-tight text-stone-900">
              Help us fix{" "}
              <span className="italic text-purple-800">{fieldLabel}</span>
            </DialogTitle>
            <DialogDescription className="text-stone-600">
              You spotted an empty cell. Anything you tell us goes
              straight to the WUA editorial team — a link, a number, a
              source, or just what you'd expect to see.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <Label
                htmlFor="ecl-hint"
                className="text-[11px] font-underground-sc tracking-wider text-stone-700"
              >
                What did you expect to see here?
              </Label>
              <Textarea
                id="ecl-hint"
                data-testid="empty-cell-help-hint"
                placeholder={`e.g. "The official rate is 3.5% — see https://…"`}
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                rows={4}
                className="mt-1"
                autoFocus
              />
              <p className="mt-1 text-[11px] text-stone-500">
                Optional but far more useful than a bare flag. Free text.
              </p>
            </div>

            <div>
              <Label
                htmlFor="ecl-relation"
                className="text-[11px] font-underground-sc tracking-wider text-stone-700"
              >
                Your relation to the university{" "}
                <span className="text-stone-400 font-normal">(optional)</span>
              </Label>
              <Select value={relation} onValueChange={setRelation}>
                <SelectTrigger
                  id="ecl-relation"
                  data-testid="empty-cell-help-relation"
                  className="mt-1"
                >
                  <SelectValue placeholder="Choose one…" />
                </SelectTrigger>
                <SelectContent>
                  {RELATION_OPTIONS.map((o) => (
                    <SelectItem
                      key={o.value}
                      value={o.value}
                      data-testid={`empty-cell-help-relation-${o.value}`}
                    >
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label
                htmlFor="ecl-email"
                className="text-[11px] font-underground-sc tracking-wider text-stone-700"
              >
                Your email{" "}
                <span className="text-stone-400 font-normal">(optional)</span>
              </Label>
              <Input
                id="ecl-email"
                data-testid="empty-cell-help-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
              />
              <p className="mt-1 text-[11px] text-stone-500">
                Not required — but including one lends credibility to
                your feedback and lets us follow up if we need a
                clarifying detail.
              </p>
            </div>

            <DialogFooter className="gap-2 sm:gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={posting}
                data-testid="empty-cell-help-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={posting}
                data-testid="empty-cell-help-submit"
                className="bg-purple-800 hover:bg-purple-900 text-white"
              >
                {posting ? "Sending…" : "Send feedback"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
